import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  MicEngine,
  type AnalysisFrame,
  type MicState,
  type MicTuning,
  type NoteEvent,
  type Onset,
} from '@audigoal/audio';
import type { DetectionParams } from '@audigoal/core';
import { configureAudio } from '@/platform/platform';
import { useMaybeAudio } from './AudioProvider';
import { pitchWorkletUrl } from './worklet-url';

/**
 * One microphone for the whole app.
 *
 * Only one thing can hold the device at a time, and asking for it twice on iOS
 * prompts twice, so the engine is created once here and exercises subscribe to
 * it. Subscribers are held in refs rather than state: a frame arrives every few
 * milliseconds, and re-rendering the tree at that rate would make the meter
 * jerk and the detector miss frames.
 */

export interface MicApi {
  engine: MicEngine;
  state: MicState;
  detail: string | null;
  supported: boolean;
  /** True once the engine is running and frames are arriving. */
  listening: boolean;
  start: (detection: DetectionParams, tuning?: MicTuning) => Promise<boolean>;
  stop: () => void;
  /** Subscribes to note events. Returns an unsubscribe. */
  onEvent: (listener: (event: NoteEvent) => void) => () => void;
  onFrame: (listener: (frame: AnalysisFrame) => void) => () => void;
  onOnset: (listener: (onset: Onset) => void) => () => void;
  /** Pauses detection while a prompt plays, so the app does not hear itself. */
  duckFor: (ms: number) => void;
}

const MicContext = createContext<MicApi | null>(null);

export function MicProvider({
  children,
  engine: injected,
}: {
  children: ReactNode;
  engine?: MicEngine;
}) {
  const [state, setState] = useState<MicState>('idle');
  const [detail, setDetail] = useState<string | null>(null);

  // A stable registry rather than refs. These are not refs to anything that
  // varies per render — they are the subscriber lists, created once and never
  // reassigned — and holding them as state keeps them out of the ref rules
  // while giving the engine's callbacks something safe to close over.
  const [listeners] = useState(() => ({
    event: new Set<(event: NoteEvent) => void>(),
    frame: new Set<(frame: AnalysisFrame) => void>(),
    onset: new Set<(onset: Onset) => void>(),
  }));

  const [engine] = useState(
    () =>
      injected ??
      new MicEngine(
        {
          onEvent: (event) => {
            for (const listener of listeners.event) listener(event);
          },
          onFrame: (frame) => {
            for (const listener of listeners.frame) listener(frame);
          },
          onOnset: (onset) => {
            for (const listener of listeners.onset) listener(onset);
          },
          onState: (next, why) => {
            setState(next);
            setDetail(why ?? null);
          },
        },
        { workletUrl: new URL(pitchWorkletUrl, window.location.href) },
      ),
  );

  // The device must be released when the app goes away, or the recording
  // indicator stays on and the browser keeps the stream open.
  useEffect(() => () => engine.stop(), [engine]);

  // The app's own sound is not the learner's. While a prompt, a preview or a
  // "show me" plays, the detector ignores what the microphone hears; without
  // this a phone on speaker answers "play the note you heard" with the very
  // note it has just played. Optional because stories and tests mount the
  // microphone without an audio provider.
  const audio = useMaybeAudio();
  useEffect(() => {
    if (audio === null) return;
    return audio.engine.onPlay((seconds) => engine.duckFor(seconds * 1000));
  }, [audio, engine]);

  // On a phone the native session is switched to play-and-record before the
  // device is opened, or iOS plays the lesson through the receiver at
  // phone-call volume. A no-op on the web.
  const start = useCallback(
    async (detection: DetectionParams, tuning: MicTuning = {}) => {
      await configureAudio('playAndRecord');
      return engine.start(detection, tuning);
    },
    [engine],
  );

  const stop = useCallback(() => {
    engine.stop();
    void configureAudio('playback');
  }, [engine]);

  // Written out one at a time rather than through a helper: a helper that calls
  // useCallback is a hook called from a nested function, which React forbids.
  const onEvent = useCallback(
    (listener: (event: NoteEvent) => void) => {
      listeners.event.add(listener);
      return () => {
        listeners.event.delete(listener);
      };
    },
    [listeners],
  );

  const onFrame = useCallback(
    (listener: (frame: AnalysisFrame) => void) => {
      listeners.frame.add(listener);
      return () => {
        listeners.frame.delete(listener);
      };
    },
    [listeners],
  );

  const onOnset = useCallback(
    (listener: (onset: Onset) => void) => {
      listeners.onset.add(listener);
      return () => {
        listeners.onset.delete(listener);
      };
    },
    [listeners],
  );

  const value = useMemo<MicApi>(
    () => ({
      engine,
      state,
      detail,
      supported: MicEngine.isSupported(),
      listening: state === 'running',
      start,
      stop,
      onEvent,
      onFrame,
      onOnset,
      duckFor: (ms: number) => engine.duckFor(ms),
    }),
    [engine, state, detail, start, stop, onEvent, onFrame, onOnset],
  );

  return <MicContext.Provider value={value}>{children}</MicContext.Provider>;
}

export function useMic(): MicApi {
  const value = useContext(MicContext);
  if (value === null) throw new Error('useMic must be used inside <MicProvider>');
  return value;
}

/**
 * The most recent pitch, for a meter.
 *
 * Kept deliberately separate from `useMic` so only the components that draw a
 * needle re-render at frame rate.
 */
export function useLivePitch(active: boolean): {
  midi: number | null;
  cents: number;
  db: number;
} {
  const mic = useMic();
  const [pitch, setPitch] = useState<{ midi: number | null; cents: number; db: number }>({
    midi: null,
    cents: 0,
    db: -Infinity,
  });

  useEffect(() => {
    if (!active) return;
    return mic.onEvent((event) => {
      if (event.type === 'pitch') {
        setPitch((previous) => ({ midi: event.midi, cents: event.cents, db: previous.db }));
      } else if (event.type === 'level') {
        setPitch((previous) => ({ ...previous, db: event.db }));
      } else if (event.type === 'noteOff') {
        setPitch((previous) => ({ ...previous, midi: null }));
      }
    });
  }, [mic, active]);

  return pitch;
}
